import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import get from 'lodash.get';
import React, { useState } from 'react';

import BlogSeo from '../components/Blog/BlogSeo';
import ContentContainer from '../components/Common/ContentContainer';
import { ArrowLink, PrimaryLink } from '../components/Common/Links';
import PageSection from '../components/Common/PageSection';
import RichTextConverter from '../components/Common/RichText/RichTextConverter';
import EventCard from '../components/EventsNews/EventCard';
import NewsCard from '../components/EventsNews/NewsCard';
import WebinarCard from '../components/EventsNews/WebinarCard';
import Layout from '../components/Layouts/Layout';
import { ReactComponent as CircleLogo } from '../images/assets/fs-monogram-circle-blk.svg';
import { ReactComponent as SquareLogo } from '../images/assets/fs-monogram-square-blk.svg';
import { ReactComponent as MainLogo } from '../images/assets/logo-fs-main.svg';
import * as styles from './eventsAndNews.module.css';

const EventsAndNews = ({ data: { page, latestNews } }) => {
  const heroIsSvg = get(page, 'heroImage.file.contentType') === 'image/svg+xml';
  const toNews = () => {
    document &&
      document.getElementById('news').scrollIntoView({
        behavior: 'smooth',
      });
  };

  const NEWS_AMOUNT = 8;
  const [amountToShow, setAmountToShow] = useState(NEWS_AMOUNT);
  const hasMore = amountToShow < latestNews.nodes.length;
  const newsArticles = latestNews.nodes.slice(0, amountToShow);

  const today = new Date();
  // const tomorrow = new Date(today);
  // tomorrow.setDate(tomorrow.getDate() + 1);
  const upcomingEvents = page.upcomingEvents?.filter((e) => {
    const eventDate = new Date(e.eventDate);
    return eventDate > today;
  });

  return (
    <Layout>
      <BlogSeo meta={page.pageMetaData} />
      <ContentContainer fullBleed className={styles.heroContainer}>
        <div className={styles.heroGrid}>
          <div className={styles.heroContent}>
            <h1>{get(page, 'headline')}</h1>
            <p>{get(page, 'subheadline')}</p>
            <ArrowLink label={get(page, 'viewAllLabel')} isArrowDown={true} onClick={toNews} />
          </div>
          <div className={styles.heroImage}>
            {heroIsSvg && <img src={get(page, 'heroImage.file.url')} alt={get(page, 'heroImage.title')} />}
            {!heroIsSvg && (
              <GatsbyImage alt={get(page, 'heroImage.title', '')} image={get(page, 'heroImage.gatsbyImageData')} />
            )}
          </div>
        </div>
      </ContentContainer>
      {upcomingEvents && upcomingEvents.length > 0 && (
        <ContentContainer className={styles.upcoming}>
          <h3>{get(page, 'upcomingEventsHeadline')}</h3>
          <div className={styles.cardGrid}>
            {upcomingEvents.map((event, i) => {
              const len = page.upcomingEvents.length;
              let isMain = i === 0 && len % 2 > 0;
              if (len > 0 && len < 3) isMain = true;
              const cardImage = event.heroImage || event?.pageMeta?.ogImage;
              const slug = event.slug === 'spark' ? '/spark' : `/resources/${event.slug}`;
              return (
                <EventCard
                  key={i}
                  image={cardImage}
                  title={event.title}
                  url={slug}
                  description={get(event, 'listingDescription.listingDescription')}
                  date={event.eventDate}
                  vertical={!isMain}
                  usesExternalRegistration={event.usesExternalRegistration}
                  externalRegistrationUrl={
                    event.extendedExternalRegistrationUrl?.extendedExternalRegistrationUrl ||
                    event.externalRegistrationUrl
                  }
                  ctaLabel={event.footerCtaLabel}
                />
              );
            })}
          </div>
        </ContentContainer>
      )}
      <PageSection className={styles.grey}>
        <h3>{get(page, 'onDemandWebinarsHeading')}</h3>
        <div className={styles.cardGrid}>
          {page.onDemandWebinars &&
            page.onDemandWebinars.map((webinar, i) => {
              return (
                <WebinarCard
                  key={i}
                  url={`/resources/${webinar.slug}`}
                  description={get(webinar, 'listingDescription.listingDescription')}
                  image={webinar.heroImage}
                  title={webinar.title}
                  ctaLabel={page.onDemandWebinarCtaLabel}
                  usesExternalRegistration={webinar.usesExternalRegistration}
                  externalRegistrationUrl={
                    webinar.extendedExternalRegistrationUrl?.extendedExternalRegistrationUrl ||
                    webinar.externalRegistrationUrl
                  }
                />
              );
            })}
        </div>
        <ArrowLink className={styles.ctaLink} label={page.onDemandWebinarResourcesLinkLabel} url={'/resources'} />
      </PageSection>
      <PageSection className={styles.blue}>
        <h3 id={'news'}>{get(page, 'newsHeadline')}</h3>
        <div className={styles.cardGrid}>
          {newsArticles &&
            newsArticles.map((news, i) => {
              return (
                <NewsCard
                  key={i}
                  title={news.title}
                  logo={get(news, 'logo')}
                  isFullStory={news.nativeFullStoryArticle}
                  logoFallback={news.logoFallback}
                  url={news.newsArticleUrl}
                  ctaLabel={page.newsCtaLabel}
                  publishDate={news.publishDate || news.createdAt}
                />
              );
            })}
        </div>
        {hasMore && (
          <div>
            <ArrowLink
              className={styles.ctaLink}
              label={'Load more'}
              onClick={() => setAmountToShow(amountToShow + NEWS_AMOUNT)}
              isArrowDown
              isLarge
            />
          </div>
        )}
      </PageSection>
      <PageSection className={styles.alabaster} wide>
        <div className={styles.wide}>
          <div className={styles.awardSection}>
            <h3>{page.awardsHeadline}</h3>
            <div className={styles.awards}>
              {page.awards.map((image, i) => {
                return <img src={image.file.url} alt={image.description} />;
              })}
            </div>
          </div>
        </div>
      </PageSection>
      <PageSection>
        <div className={styles.brandWrapper}>
          <div className={styles.content}>
            <h3>{page.brandAssetsHeadline}</h3>
            <h4>{page.brandAssetsSubheadline}</h4>
            <RichTextConverter copy={page.inquiryStatement} />
          </div>
          <div className={styles.assets}>
            <div className={styles.logos}>
              <MainLogo /> <SquareLogo /> <CircleLogo />
            </div>
            <PrimaryLink
              className={styles.assetBtn}
              label={'Download the Assets'}
              url={page.assets.file.url}
              backgroundColor={'white'}
              isButton
            />
          </div>
        </div>
      </PageSection>
    </Layout>
  );
};

export default EventsAndNews;

export const pageQuery = graphql`
  query($id: String!) {
    page: contentfulEventsAndNewsHomepage(id: { eq: $id }) {
      ...EventsAndNewsHomepage
    }
    latestNews: allContentfulNewsArticle(sort: { fields: publishDate, order: DESC }) {
      nodes {
        ...AllNewsArticles
      }
    }
    siteSettings: contentfulBlogSettings(contentful_id: { eq: "6KdgnoVe8iL9MGRTTPgqyQ" }) {
      ...SiteSettings
    }
  }
`;
